import type { MessageId, PreviewAnnotationPayload, ScopedThreadRef } from "@t3tools/contracts";

import type { ComposerAttachment, DraftId } from "./composerDraftStore";
import type { ElementContextDraft } from "./lib/elementContext";
import type { TerminalContextDraft } from "./lib/terminalContext";
import type { ReviewCommentContext } from "./reviewCommentContext";
import type { PendingDraftSubmissionIdentity } from "./components/ChatView.logic";

export interface PendingDraftSubmission extends PendingDraftSubmissionIdentity {
  readonly threadKey: string;
  readonly target: ScopedThreadRef | DraftId;
  readonly prompt: string;
  readonly images: ReadonlyArray<ComposerAttachment>;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly elementContexts: ReadonlyArray<ElementContextDraft>;
  readonly previewAnnotations: ReadonlyArray<PreviewAnnotationPayload>;
  readonly reviewComments: ReadonlyArray<ReviewCommentContext>;
}

// Process-local by design: it survives draft-route component remounts, but is
// neither persisted nor rendered as an optimistic message.
const pendingDraftSubmissions = new Map<MessageId, PendingDraftSubmission>();

export function pendingDraftSubmissionEntries(): IterableIterator<
  [MessageId, PendingDraftSubmission]
> {
  return pendingDraftSubmissions.entries();
}

export function writePendingDraftSubmission(value: PendingDraftSubmission): void {
  pendingDraftSubmissions.set(value.submissionId, value);
}

export function clearPendingDraftSubmission(submissionId: MessageId): boolean {
  return pendingDraftSubmissions.delete(submissionId);
}

/** Releases retry snapshots only when the containing thread is definitively deleted. */
export function clearPendingDraftSubmissionsForThread(threadKey: string): number {
  let cleared = 0;
  for (const [submissionId, snapshot] of pendingDraftSubmissions) {
    if (snapshot.threadKey !== threadKey) continue;
    pendingDraftSubmissions.delete(submissionId);
    cleared += 1;
  }
  return cleared;
}
